import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ReportPage from "@/pages/ReportPage";
import AgeingPage from "@/pages/AgeingPage";
import StockRecoPage from "@/pages/StockRecoPage";
import StockAgeingPage from "@/pages/StockAgeingPage";
import SalesPerformancePage from "@/pages/SalesPerformancePage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F7F9] text-slate-500">
        <div className="animate-pulse text-sm tracking-widest uppercase">Loading Dee Piping Insights…</div>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  // drag-to-pan for wide report tables (.report-scroll): hold the mouse and drag to scroll
  useEffect(() => {
    let el = null, sx = 0, sy = 0, sl = 0, st = 0, moved = false;
    const down = (e) => {
      const t = e.target.closest?.(".report-scroll");
      if (!t || e.button !== 0) return;
      if (e.target.closest("input, textarea, select, button, a")) return;
      const r = t.getBoundingClientRect();
      if (e.clientX > r.left + t.clientWidth || e.clientY > r.top + t.clientHeight) return; // native scrollbar press
      el = t; sx = e.pageX; sy = e.pageY; sl = t.scrollLeft; st = t.scrollTop; moved = false;
    };
    const move = (e) => {
      if (!el) return;
      const dx = e.pageX - sx, dy = e.pageY - sy;
      if (!moved && Math.abs(dx) + Math.abs(dy) > 6) { moved = true; el.classList.add("dragging"); }
      if (moved) { el.scrollLeft = sl - dx; el.scrollTop = st - dy; e.preventDefault(); }
    };
    const up = () => {
      if (el && moved) {
        const suppress = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
        document.addEventListener("click", suppress, { capture: true, once: true });
        setTimeout(() => document.removeEventListener("click", suppress, { capture: true }), 80);
        el.classList.remove("dragging");
      }
      el = null; moved = false;
    };
    document.addEventListener("mousedown", down);
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    // keep each table's bottom (and horizontal scrollbar) inside the viewport
    let raf = 0;
    const size = () => {
      raf = 0;
      document.querySelectorAll(".report-scroll").forEach((n) => {
        const top = n.getBoundingClientRect().top;
        n.style.maxHeight = `${Math.max(280, window.innerHeight - Math.max(top, 64) - 24)}px`;
      });
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(size); };
    window.addEventListener("scroll", queue, true);
    window.addEventListener("resize", queue);
    const iv = setInterval(queue, 800);
    queue();
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      window.removeEventListener("scroll", queue, true);
      window.removeEventListener("resize", queue);
      clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <AuthProvider>
      <Toaster position="top-right" theme="light" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="reports/ar-ageing" element={<AgeingPage side="AR" />} />
            <Route path="reports/ap-ageing" element={<AgeingPage side="AP" />} />
            <Route path="reports/stock-reco" element={<StockRecoPage />} />
            <Route path="reports/stock-ageing" element={<StockAgeingPage />} />
            <Route path="reports/sales-performance" element={<SalesPerformancePage />} />
            <Route path="reports/:reportKey" element={<ReportPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
